// The timeline of a product: its programmes as spans and its incidents as
// events on one axis of time. Pure functions: the timeline section draws
// what they return.

import { codeWords } from "../Detail/format";
import { RelationRow, rolesOf } from "./productRelations";

// How exactly a record's date is known
export type Precision = "year" | "month" | "day";

// A date as the time it may stand for: a year-only date is the whole year,
// a month the whole month, a day that day. `lo` is inclusive, `hi`
// exclusive, both in milliseconds.
export interface DateSpan {
  lo: number;
  hi: number;
  precision: Precision;
}

const DAY = 24 * 3600 * 1000;
export const YEAR = 365 * DAY;

// A date of the records ("2026", "2026-06", "2026-06-12") as the time it
// stands for; null for one that does not read as a date
export const dateSpan = (date: string | null | undefined): DateSpan | null => {
  const match = /^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?/.exec(date ?? "");
  if (!match) {
    return null;
  }
  const [year, month, day] = [match[1], match[2], match[3]].map((part) =>
    part === undefined ? undefined : Number(part),
  ) as [number, number | undefined, number | undefined];
  if (month === undefined) {
    return { lo: Date.UTC(year, 0, 1), hi: Date.UTC(year + 1, 0, 1), precision: "year" };
  }
  if (day === undefined) {
    return {
      lo: Date.UTC(year, month - 1, 1),
      hi: Date.UTC(year, month, 1),
      precision: "month",
    };
  }
  const lo = Date.UTC(year, month - 1, day);
  return { lo, hi: lo + DAY, precision: "day" };
};

// How a programme is drawn:
// - "span": from its start to its end
// - "open": for one running with no end, from its start to its newest
//   source, then fading for a while after (see `OpenReach`)
// - "planned": hollow, for one that has not begun
// - "point": its start alone, for one completed or cancelled with no end
// - "mentioned": for one no source dates, the days its sources were
//   published, over a faint band from the first to the last
export type ProgrammeShape = "span" | "open" | "planned" | "point" | "mentioned";

// How far an open programme is known to run: to the newest source that
// mentions it, never before its start; past that it may have run on, so it
// fades over a while, and stops at today
export interface OpenReach {
  // The day of its newest source, or the end of its start where no source
  // is later
  seen: number;
  // Where the fade ends
  fadeTo: number;
}

// How long an open programme fades for past its newest source: the same
// for every kind of programme, as types tell too little of how long one
// runs
export const OPEN_FADE = 2 * YEAR;

export interface ProgrammeMark {
  row: RelationRow;
  // Every role the programme plays, where it is linked twice
  roles: string[];
  // For a mentioned programme, the day of its first mention
  start: DateSpan;
  // For a mentioned programme, the day of its last mention
  end: DateSpan | null;
  shape: ProgrammeShape;
  // The days its sources were published, earliest first; drawn only for a
  // mentioned programme
  mentions: number[];
  // For an open programme, how far it is known to run; null for the others
  reach: OpenReach | null;
}

export interface IncidentMark {
  row: RelationRow;
  // Every role the product plays in it, where it is linked twice
  roles: string[];
  at: DateSpan;
}

export interface IncidentLane {
  // The incident type in words, or the lane of the rest
  type: string;
  marks: IncidentMark[];
}

export interface TimelineModel {
  // Earliest start first
  programmes: ProgrammeMark[];
  // A lane per incident type, the most frequent first
  lanes: IncidentLane[];
  // The time the axis spans, with a margin either side
  from: number;
  to: number;
  // The records without a date, by kind: a programme placed by its
  // mentions is not one
  undated: { programmes: number; incidents: number };
}

// Incident lanes drawn before the rest go into one
export const MAX_LANES = 6;
export const OTHER_LANE = "other types";

const STATUS_PLANNED = "planned";
const STATUS_ENDED = new Set(["completed", "cancelled"]);

// The shape of a programme by its dates and its status
export const programmeShape = (
  start: DateSpan,
  end: DateSpan | null,
  status: string[],
  today: number,
): ProgrammeShape => {
  if (status.includes(STATUS_PLANNED) || start.lo > today) {
    return "planned";
  }
  if (end) {
    return "span";
  }
  return status.some((value) => STATUS_ENDED.has(value)) ? "point" : "open";
};

// How far an open programme is known to run, by its start and the days its
// sources were published
export const openReach = (start: DateSpan, mentions: number[], today: number): OpenReach => {
  const newest = mentions.length > 0 ? mentions[mentions.length - 1] + DAY : 0;
  const seen = Math.min(Math.max(start.hi, newest), today);
  return { seen, fadeTo: Math.min(seen + OPEN_FADE, today) };
};

interface Entry {
  row: RelationRow;
  roles: string[];
  // The publication days of the sources of all its links
  mentions: number[];
}

// One record per entity: a programme linked under two relations is one
// programme with two roles, mentioned by the sources of both
const byEntity = (rows: RelationRow[]): Entry[] => {
  const entries = new Map<string, Entry>();
  for (const row of rows) {
    const days = row.sources.flatMap((source) => {
      const day = dateSpan(source.date);
      return day ? [day.lo] : [];
    });
    const entry = entries.get(row.entity.id);
    if (entry) {
      entry.roles = Array.from(new Set([...entry.roles, ...rolesOf(row)]));
      entry.mentions = [...entry.mentions, ...days];
    } else {
      entries.set(row.entity.id, { row, roles: rolesOf(row), mentions: days });
    }
  }
  return Array.from(entries.values(), (entry) => ({
    ...entry,
    mentions: Array.from(new Set(entry.mentions)).sort((left, right) => left - right),
  }));
};

/**
 * The timeline of a product's programmes and incidents; null when fewer
 * than two of them can be placed. A programme no source dates is placed by
 * the days its sources were published.
 *
 * @param programmes - The programme rows of the product
 * @param incidents - The incident rows of the product
 * @param today - The time "now" stands at: no open programme runs past it
 */
export const timelineModel = (
  programmes: RelationRow[],
  incidents: RelationRow[],
  today: number,
): TimelineModel | null => {
  const programmeEntries = byEntity(programmes);
  const incidentEntries = byEntity(incidents);

  const programmeMarks = programmeEntries
    .flatMap(({ row, roles, mentions }): ProgrammeMark[] => {
      const start = dateSpan(row.start) ?? dateSpan(row.end);
      if (!start) {
        // No date stated: placed by when its sources were published
        if (mentions.length === 0) {
          return [];
        }
        const first = mentions[0];
        const last = mentions[mentions.length - 1];
        return [
          {
            row,
            roles,
            start: { lo: first, hi: first + DAY, precision: "day" },
            end: { lo: last, hi: last + DAY, precision: "day" },
            shape: "mentioned",
            mentions,
            reach: null,
          },
        ];
      }
      const end = row.start ? dateSpan(row.end) : null;
      const closed = end && end.hi > start.lo ? end : null;
      const shape = programmeShape(start, closed, row.status ?? [], today);
      return [
        {
          row,
          roles,
          start,
          end: closed,
          shape,
          mentions,
          reach: shape === "open" ? openReach(start, mentions, today) : null,
        },
      ];
    })
    .sort((left, right) => left.start.lo - right.start.lo);

  const incidentMarks = incidentEntries.flatMap(({ row, roles }): IncidentMark[] => {
    const at = dateSpan(row.start) ?? dateSpan(row.end);
    return at ? [{ row, roles, at }] : [];
  });

  if (programmeMarks.length + incidentMarks.length < 2) {
    return null;
  }

  // A lane per type the incident names first, the most frequent first; the
  // ones past the last lane, and the incidents of no type, share one
  const byType = new Map<string, IncidentMark[]>();
  for (const mark of incidentMarks) {
    const type = mark.row.types?.[0] ? codeWords(mark.row.types[0]) : OTHER_LANE;
    byType.set(type, [...(byType.get(type) ?? []), mark]);
  }
  const sorted = Array.from(byType)
    .filter(([type]) => type !== OTHER_LANE)
    .sort(
      ([left, leftMarks], [right, rightMarks]) =>
        rightMarks.length - leftMarks.length || left.localeCompare(right),
    );
  const rest = [
    ...sorted.slice(MAX_LANES).flatMap(([, marks]) => marks),
    ...(byType.get(OTHER_LANE) ?? []),
  ];
  const lanes: IncidentLane[] = [
    ...sorted.slice(0, MAX_LANES).map(([type, marks]) => ({ type, marks })),
    ...(rest.length > 0 ? [{ type: OTHER_LANE, marks: rest }] : []),
  ];

  // The axis runs over every date, and to the end of an open programme's
  // fade
  const times = [
    ...programmeMarks.flatMap((mark) => [
      mark.start.lo,
      mark.reach?.fadeTo ?? (mark.end ?? mark.start).hi,
    ]),
    ...incidentMarks.flatMap((mark) => [mark.at.lo, mark.at.hi]),
  ];
  const earliest = Math.min(...times);
  const latest = Math.max(...times);
  const pad = Math.max((latest - earliest) * 0.03, 30 * DAY);

  return {
    programmes: programmeMarks,
    lanes,
    from: earliest - pad,
    to: latest + pad,
    undated: {
      programmes: programmeEntries.length - programmeMarks.length,
      incidents: incidentEntries.length - incidentMarks.length,
    },
  };
};

// The years an axis between two times marks: every year over a short span,
// every second or fifth over a long one
export const yearTicks = (from: number, to: number): number[] => {
  const first = new Date(from).getUTCFullYear() + 1;
  const last = new Date(to).getUTCFullYear();
  const span = last - first;
  const step = span > 24 ? 5 : span > 12 ? 2 : 1;
  const ticks: number[] = [];
  for (let year = first; year <= last; year += 1) {
    if (year % step === 0 || step === 1) {
      ticks.push(year);
    }
  }
  return ticks;
};

// A record's date as a tooltip reads it, with how exactly it is known
export const dateWords = (row: RelationRow): string => {
  const words =
    row.start && row.end && row.start !== row.end
      ? `${row.start} – ${row.end}`
      : (row.start ?? row.end ?? "");
  return words;
};
